import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence, Reorder } from 'framer-motion';
import { 
  Music, Play, Pause, SkipForward, Plus, Trash2, Check, X, 
  Download, Clock, Users, Disc3, GripVertical, ExternalLink,
  Loader2, Power, PowerOff, QrCode, Eye, EyeOff, ThumbsUp, AlertCircle,
  History, RotateCcw, Calendar, ChevronDown, ChevronUp, Share2, BarChart3,
  Sparkles, Monitor, Megaphone, Projector, CalendarClock
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import SessionSummary from '../components/SessionSummary';

import { API_URL } from '../utils/api';

// Current version for release notes
const CURRENT_VERSION = '1.2.0';
const RELEASE_NOTES = [
  {
    version: '1.2.0',
    date: 'March 2026',
    changes: [
      { type: 'new', text: 'TV Display Mode - Open a full-screen view optimized for large screens/TVs' },
      { type: 'new', text: 'Add dances "as typed" - Add any dance even if not found in search' },
      { type: 'improved', text: 'Larger dance selection dropdown - Easier to select from search results' },
      { type: 'improved', text: 'Better source labels - Now shows "BootStepper" and "CopperKnob" in full' },
    ]
  },
  {
    version: '1.1.0',
    date: 'February 2026',
    changes: [
      { type: 'new', text: 'Session history - View and recall past sessions' },
      { type: 'new', text: 'Dance requests from dancers with voting' },
      { type: 'improved', text: 'Drag and drop playlist reordering' },
    ]
  }
];

export default function DJConsolePage({ mode = 'club' }) {
  const { clubSlug, djSlug } = useParams();
  const entitySlug = mode === 'dj' ? djSlug : clubSlug;
  const navigate = useNavigate();
  const { user } = useAuth();
  const [sessionData, setSessionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [starting, setStarting] = useState(false);
  const [ending, setEnding] = useState(false);
  const [sessionName, setSessionName] = useState('');
  const [venue, setVenue] = useState('');
  const [upcomingDances, setUpcomingDances] = useState([]);
  const [showExport, setShowExport] = useState(false);
  const [exportData, setExportData] = useState(null);
  
  // Announcement state
  const [announcement, setAnnouncement] = useState('');
  const [savingAnnouncement, setSavingAnnouncement] = useState(false);
  
  // Release notes state
  const [showReleaseNotes, setShowReleaseNotes] = useState(false);
  
  // Manual add dance state
  const [showAddDance, setShowAddDance] = useState(false);
  const [manualDanceName, setManualDanceName] = useState('');
  const [manualDanceSearch, setManualDanceSearch] = useState([]);
  const [selectedManualDance, setSelectedManualDance] = useState(null);
  const [searchingDance, setSearchingDance] = useState(false);
  
  // Session history state
  const [showHistory, setShowHistory] = useState(false);
  const [pastSessions, setPastSessions] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [recalling, setRecalling] = useState(false);
  const [togglingRequests, setTogglingRequests] = useState(false);
  
  // Scheduled resume state
  const [showScheduleResume, setShowScheduleResume] = useState(false);
  const [scheduledResumeDate, setScheduledResumeDate] = useState('');
  const [scheduledResumeTime, setScheduledResumeTime] = useState('');
  const [savingScheduledResume, setSavingScheduledResume] = useState(false);
  
  // Session summary state
  const [showSummary, setShowSummary] = useState(false);
  const [summarySession, setSummarySession] = useState(null);
  
  // Analytics state
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);
  
  // Schedule state
  const [showSchedule, setShowSchedule] = useState(false);
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [scheduleItems, setScheduleItems] = useState([]);
  const [scheduleEventDate, setScheduleEventDate] = useState('');
  const [newScheduleTime, setNewScheduleTime] = useState('');
  const [newScheduleTitle, setNewScheduleTitle] = useState('');
  const [newScheduleDesc, setNewScheduleDesc] = useState('');
  const [savingSchedule, setSavingSchedule] = useState(false);
  
  // Check if user has seen current release notes
  useEffect(() => {
    const seenVersion = localStorage.getItem('dj_console_seen_version');
    if (seenVersion !== CURRENT_VERSION) {
      setShowReleaseNotes(true);
    }
  }, []);
  
  const dismissReleaseNotes = () => {
    localStorage.setItem('dj_console_seen_version', CURRENT_VERSION);
    setShowReleaseNotes(false);
  };
  const [loadingSummary, setLoadingSummary] = useState(false);

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  }, []);

  // Generate API base URL based on mode (club vs dj)
  const apiBase = mode === 'dj' 
    ? `${API_URL}/api/djs/${entitySlug}/dj`
    : `${API_URL}/api/clubs/${entitySlug}/dj`;

  // Generate live page URL based on mode
  const livePageUrl = mode === 'dj'
    ? `${window.location.origin}/dj/${entitySlug}/live`
    : `${window.location.origin}/club/${entitySlug}/dj-live`;

  // Generate display page URL based on mode
  const displayPageUrl = mode === 'dj'
    ? `${window.location.origin}/dj/${entitySlug}/display`
    : `${window.location.origin}/dj/display/${entitySlug}`;

  // Generate projector page URL based on mode
  const projectorPageUrl = mode === 'dj'
    ? `${window.location.origin}/dj/${entitySlug}/projector`
    : `${window.location.origin}/dj/projector/${entitySlug}`;

  // Track consecutive failures for resilient error handling
  const fetchFailureCountRef = React.useRef(0);
  const MAX_FETCH_FAILURES = 3;

  // Fetch session data
  const fetchSession = useCallback(async () => {
    try {
      const response = await fetch(`${apiBase}/session`, {
        headers: getAuthHeaders()
      });
      
      if (response.status === 403) {
        setError(mode === 'dj' ? 'You do not have access to this DJ console' : 'You do not have DJ access to this club');
        setLoading(false);
        return;
      }
      
      if (!response.ok) {
        throw new Error(`Server error: ${response.status}`);
      }
      
      const text = await response.text();
      if (!text) {
        throw new Error('Empty response');
      }
      const data = JSON.parse(text);
      
      setSessionData(data);
      if (data.upcoming_dances) {
        setUpcomingDances(data.upcoming_dances);
      }
      // Initialize announcement from session data
      if (data.announcement !== undefined) {
        setAnnouncement(data.announcement || '');
      }
      // Initialize schedule from session data
      if (data.schedule_enabled !== undefined) {
        setScheduleEnabled(data.schedule_enabled);
      }
      if (data.schedule_items) {
        setScheduleItems(data.schedule_items);
      }
      if (data.event_date !== undefined) {
        setScheduleEventDate(data.event_date || '');
      }
      setError(null);
      fetchFailureCountRef.current = 0; // Reset on success
    } catch (err) {
      fetchFailureCountRef.current += 1;
      // Only show error after multiple consecutive failures
      if (fetchFailureCountRef.current >= MAX_FETCH_FAILURES) {
        setError('Connection lost. Retrying...');
      }
      console.warn(`DJ Console fetch attempt ${fetchFailureCountRef.current} failed:`, err.message);
    } finally {
      setLoading(false);
    }
  }, [apiBase, getAuthHeaders, mode]);

  // Check for scheduled resume
  const checkScheduledResume = useCallback(async () => {
    if (!sessionData?.active || sessionData?.accepting_requests !== false) return;
    if (!sessionData?.scheduled_resume_at) return;
    
    try {
      const response = await fetch(`${apiBase}/session/check-scheduled-resume`);
      if (response.ok) {
        const data = await response.json();
        if (data.resumed) {
          toast.success('Requests automatically resumed!');
          setScheduledResumeDate('');
          setScheduledResumeTime('');
          fetchSession();
        }
      }
    } catch (err) {
      console.error('Error checking scheduled resume:', err);
    }
  }, [apiBase, sessionData, fetchSession]);

  // Poll for updates
  useEffect(() => {
    fetchSession();
    const interval = setInterval(() => {
      fetchSession();
      checkScheduledResume();
    }, 3000);
    return () => clearInterval(interval);
  }, [fetchSession, checkScheduledResume]);

  // Fetch past sessions
  const fetchPastSessions = async () => {
    setLoadingHistory(true);
    try {
      const response = await fetch(`${apiBase}/sessions`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        // Filter to only show ended sessions
        setPastSessions(data.filter(s => s.status === 'ended'));
      }
    } catch (err) {
      console.error('Failed to fetch sessions:', err);
    } finally {
      setLoadingHistory(false);
    }
  };

  // Fetch analytics data
  const fetchAnalytics = async () => {
    setLoadingAnalytics(true);
    try {
      const response = await fetch(`${apiBase}/analytics`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setAnalyticsData(data);
      } else {
        toast.error('Failed to load analytics');
      }
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
      toast.error('Failed to load analytics');
    } finally {
      setLoadingAnalytics(false);
    }
  };

  // Load analytics when modal opens
  useEffect(() => {
    if (showAnalytics) {
      fetchAnalytics();
    }
  }, [showAnalytics]);

  // Load past sessions when history panel opens
  useEffect(() => {
    if (showHistory) {
      fetchPastSessions();
    }
  }, [showHistory, entitySlug]);

  // Recall a past session
  const recallSession = async (sessionId) => {
    if (!window.confirm('This will start a new session with all dances from the selected session pre-loaded in the queue. Continue?')) {
      return;
    }
    
    setRecalling(true);
    try {
      const response = await fetch(`${apiBase}/sessions/${sessionId}/recall`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Failed to recall session');
      }
      
      toast.success('Session recalled! All dances loaded in queue.');
      setShowHistory(false);
      fetchSession();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setRecalling(false);
    }
  };

  // Search for dances (manual add)
  useEffect(() => {
    const searchDances = async () => {
      if (manualDanceName.length < 2) {
        setManualDanceSearch([]);
        return;
      }
      
      setSearchingDance(true);
      try {
        const response = await fetch(`${API_URL}/api/dances/search?q=${encodeURIComponent(manualDanceName)}&limit=15`);
        if (response.ok) {
          const results = await response.json();
          setManualDanceSearch(results);
        }
      } catch (err) {
        console.error('Search error:', err);
      } finally {
        setSearchingDance(false);
      }
    };

    const debounce = setTimeout(searchDances, 300);
    return () => clearTimeout(debounce);
  }, [manualDanceName]);

  // Start session
  const startSession = async () => {
    setStarting(true);
    try {
      const response = await fetch(`${apiBase}/session/start`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ 
          session_name: sessionName || null,
          venue: venue || null
        })
      });
      
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Failed to start session');
      }
      
      toast.success('Session started!');
      setVenue(''); // Reset venue after starting
      fetchSession();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setStarting(false);
    }
  };

  // End session
  const endSession = async () => {
    if (!window.confirm('Are you sure you want to end this session?')) return;
    
    // Save current session data before ending
    const endedSessionData = { ...sessionData };
    
    setEnding(true);
    try {
      const response = await fetch(`${apiBase}/session/end`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      
      if (!response.ok) throw new Error('Failed to end session');
      
      toast.success('Session ended');
      
      // Show summary with the ended session data
      setSummarySession(endedSessionData);
      setShowSummary(true);
      
      fetchSession();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setEnding(false);
    }
  };

  // View summary for a past session
  const viewSessionSummary = async (sessionId) => {
    setLoadingSummary(true);
    try {
      const response = await fetch(`${apiBase}/sessions/${sessionId}`, {
        headers: getAuthHeaders()
      });
      
      if (!response.ok) throw new Error('Failed to load session');
      
      const data = await response.json();
      setSummarySession(data);
      setShowSummary(true);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoadingSummary(false);
    }
  };

  // Save announcement
  const saveAnnouncement = async (value = null) => {
    const textToSave = value !== null ? value : announcement.trim();
    setSavingAnnouncement(true);
    try {
      const url = `${apiBase}/announcement`;
      console.log('Saving announcement to:', url);
      
      const response = await fetch(url, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ announcement: textToSave })
      });
      
      console.log('Response status:', response.status);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Announcement error:', errorData);
        throw new Error(errorData.detail || `Failed to save announcement (${response.status})`);
      }
      
      toast.success(textToSave ? 'Announcement saved' : 'Announcement cleared');
    } catch (err) {
      console.error('Announcement save error:', err);
      toast.error(err.message);
    } finally {
      setSavingAnnouncement(false);
    }
  };

  // ===== SCHEDULE MANAGEMENT =====
  
  const saveEventDate = async (dateValue) => {
    setSavingSchedule(true);
    try {
      const response = await fetch(`${apiBase}/schedule/date`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ event_date: dateValue || null })
      });
      
      if (!response.ok) throw new Error('Failed to save event date');
      
      toast.success(dateValue ? 'Event date saved' : 'Event date cleared');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSavingSchedule(false);
    }
  };
  
  const addScheduleItem = async () => {
    if (!newScheduleTime || !newScheduleTitle.trim()) {
      toast.error('Please enter time and title');
      return;
    }
    
    setSavingSchedule(true);
    try {
      const response = await fetch(`${apiBase}/schedule/item`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          time: newScheduleTime,
          title: newScheduleTitle.trim(),
          description: newScheduleDesc.trim() || null
        })
      });
      
      if (!response.ok) throw new Error('Failed to add schedule item');
      
      toast.success('Schedule item added');
      setNewScheduleTime('');
      setNewScheduleTitle('');
      setNewScheduleDesc('');
      fetchSession();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSavingSchedule(false);
    }
  };
  
  const removeScheduleItem = async (itemId) => {
    try {
      const response = await fetch(`${apiBase}/schedule/item/${itemId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      
      if (!response.ok) throw new Error('Failed to remove schedule item');
      
      toast.success('Schedule item removed');
      fetchSession();
    } catch (err) {
      toast.error(err.message);
    }
  };
  
  const toggleScheduleEnabled = async () => {
    setSavingSchedule(true);
    try {
      const response = await fetch(`${apiBase}/schedule`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          schedule_enabled: !scheduleEnabled,
          schedule_items: scheduleItems
        })
      });
      
      if (!response.ok) throw new Error('Failed to toggle schedule');
      
      setScheduleEnabled(!scheduleEnabled);
      toast.success(scheduleEnabled ? 'Schedule hidden from display' : 'Schedule shown on display');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSavingSchedule(false);
    }
  };

  // Toggle accepting requests
  const toggleAcceptingRequests = async () => {
    setTogglingRequests(true);
    try {
      const response = await fetch(`${apiBase}/session/toggle-requests`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      
      if (!response.ok) throw new Error('Failed to toggle requests');
      
      const data = await response.json();
      toast.success(data.message);
      
      // Clear scheduled resume inputs if resuming
      if (data.accepting_requests) {
        setScheduledResumeDate('');
        setScheduledResumeTime('');
      }
      
      fetchSession();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setTogglingRequests(false);
    }
  };
  
  // Save scheduled resume time
  const saveScheduledResume = async () => {
    if (!scheduledResumeDate || !scheduledResumeTime) {
      toast.error('Please set both date and time');
      return;
    }
    
    setSavingScheduledResume(true);
    try {
      // Combine date and time into ISO string
      const scheduledDateTime = new Date(`${scheduledResumeDate}T${scheduledResumeTime}:00`);
      const isoString = scheduledDateTime.toISOString();
      
      const response = await fetch(`${apiBase}/session/schedule-resume`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ scheduled_resume_at: isoString })
      });
      
      if (!response.ok) throw new Error('Failed to schedule resume');
      
      const data = await response.json();
      toast.success(`Requests will auto-resume at ${scheduledResumeTime} on ${scheduledResumeDate}`);
      setShowScheduleResume(false);
      fetchSession();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSavingScheduledResume(false);
    }
  };
  
  // Clear scheduled resume
  const clearScheduledResume = async () => {
    setSavingScheduledResume(true);
    try {
      const response = await fetch(`${apiBase}/session/schedule-resume`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ scheduled_resume_at: null })
      });
      
      if (!response.ok) throw new Error('Failed to clear schedule');
      
      toast.success('Scheduled resume cleared');
      setScheduledResumeDate('');
      setScheduledResumeTime('');
      fetchSession();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSavingScheduledResume(false);
    }
  };

  // Add request to queue
  const addToQueue = async (request) => {
    const newQueue = [...upcomingDances, request];
    setUpcomingDances(newQueue);
    
    try {
      await fetch(`${apiBase}/queue`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ upcoming_dances: newQueue })
      });
      fetchSession();
    } catch (err) {
      toast.error('Failed to update queue');
      setUpcomingDances(upcomingDances);
    }
  };

  // Manually add dance to queue
  const addManualDance = async (playNow = false) => {
    if (!manualDanceName.trim() && !selectedManualDance) {
      toast.error('Please enter a dance name');
      return;
    }

    const dance = selectedManualDance || {
      dance_name: manualDanceName.trim(),
      level: null,
      count: null,
      wall: null,
      dance_url: null
    };

    const manualEntry = {
      request_id: `manual_${Date.now()}`,
      dance_name: dance.name || dance.dance_name,
      level: dance.level,
      count: dance.count,
      wall: dance.wall,
      dance_url: dance.url || dance.dance_url,
      requester_name: 'DJ',
      votes: 0,
      is_manual: true
    };

    if (playNow) {
      // Set as now playing directly
      try {
        const session = sessionData;
        
        // If there's a current dance, move it to played
        if (session.current_dance) {
          await fetch(`${apiBase}/finish-current`, {
            method: 'POST',
            headers: getAuthHeaders()
          });
        }
        
        // Update session with new current dance
        await fetch(`${apiBase}/queue`, {
          method: 'PUT',
          headers: getAuthHeaders(),
          body: JSON.stringify({ 
            upcoming_dances: upcomingDances,
            current_dance: manualEntry
          })
        });
        
        toast.success('Now playing: ' + manualEntry.dance_name);
        fetchSession();
      } catch (err) {
        toast.error('Failed to set now playing');
      }
    } else {
      // Add to queue
      const newQueue = [...upcomingDances, manualEntry];
      setUpcomingDances(newQueue);
      
      try {
        await fetch(`${apiBase}/queue`, {
          method: 'PUT',
          headers: getAuthHeaders(),
          body: JSON.stringify({ upcoming_dances: newQueue })
        });
        toast.success('Added to queue: ' + manualEntry.dance_name);
        fetchSession();
      } catch (err) {
        toast.error('Failed to add to queue');
        setUpcomingDances(upcomingDances);
      }
    }

    // Reset form
    setManualDanceName('');
    setSelectedManualDance(null);
    setManualDanceSearch([]);
    setShowAddDance(false);
  };

  // Remove from queue
  const removeFromQueue = async (requestId) => {
    const newQueue = upcomingDances.filter(d => d.request_id !== requestId);
    setUpcomingDances(newQueue);
    
    try {
      await fetch(`${apiBase}/request/${requestId}/status`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ status: 'pending' })
      });
      
      await fetch(`${apiBase}/queue`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ upcoming_dances: newQueue })
      });
      fetchSession();
    } catch (err) {
      toast.error('Failed to update queue');
    }
  };

  // Decline request
  const declineRequest = async (requestId) => {
    try {
      await fetch(`${apiBase}/request/${requestId}/status`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ status: 'declined' })
      });
      toast.success('Request declined');
      fetchSession();
    } catch (err) {
      toast.error('Failed to decline request');
    }
  };

  // Set the DJ's selected recording (song) for a request — platform-independent metadata
  const selectRequestSong = async (requestId, song) => {
    try {
      const response = await fetch(`${apiBase}/request/${requestId}/song`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          title: song.title,
          artist: song.artist || null,
          music_id: song.music_id || null
        })
      });
      if (!response.ok) throw new Error('Failed to save song selection');
      toast.success(`Song set: ${song.title}`);
      fetchSession();
    } catch (err) {
      toast.error('Failed to save song selection');
    }
  };


  // Set now playing
  const setNowPlaying = async (request) => {
    try {
      await fetch(`${apiBase}/now-playing`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          dance: {
            request_id: request.request_id,
            dance_name: request.dance_name,
            dance_url: request.dance_url,
            level: request.level,
            count: request.count,
            wall: request.wall,
            requester_name: request.requester_name,
            is_manual: request.is_manual || false
          }
        })
      });
      
      // Remove from upcoming if it was there
      const newQueue = upcomingDances.filter(d => d.request_id !== request.request_id);
      setUpcomingDances(newQueue);
      
      toast.success('Now playing: ' + request.dance_name);
      fetchSession();
    } catch (err) {
      toast.error('Failed to set now playing');
    }
  };

  // Play next from queue
  const playNext = async () => {
    if (upcomingDances.length === 0) {
      toast.error('Queue is empty');
      return;
    }
    
    const next = upcomingDances[0];
    await setNowPlaying(next);
  };

  // Finish current dance
  const finishCurrent = async () => {
    try {
      await fetch(`${apiBase}/finish-current`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      toast.success('Dance finished');
      fetchSession();
    } catch (err) {
      toast.error('Failed to finish dance');
    }
  };

  // Update queue order
  const handleReorder = async (newOrder) => {
    setUpcomingDances(newOrder);
    
    try {
      await fetch(`${apiBase}/queue`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ upcoming_dances: newOrder })
      });
    } catch (err) {
      toast.error('Failed to reorder queue');
    }
  };

  // Export session
  const handleExport = async () => {
    try {
      const response = await fetch(`${apiBase}/session/export`, {
        headers: getAuthHeaders()
      });
      
      if (!response.ok) throw new Error('Failed to export');
      
      const data = await response.json();
      setExportData(data);
      setShowExport(true);
    } catch (err) {
      toast.error(err.message);
    }
  };

  // Download as CSV
  const downloadCSV = () => {
    if (!exportData) return;
    
    const headers = ['#', 'Dance Name', 'Level', 'Count', 'Wall', 'Requested By', 'Votes'];
    const rows = exportData.played_dances.map((d, i) => [
      i + 1,
      d.dance_name,
      d.level || '',
      d.count || '',
      d.wall || '',
      d.requester_name,
      d.votes
    ]);
    
    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${exportData.session_name || 'session'}_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-violet-400 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <p className="text-red-400 mb-4">{error}</p>
          <Button onClick={() => navigate('/dashboard')} variant="outline">
            Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Header */}
      <div className="bg-slate-900 border-b border-slate-800 sticky top-0 z-40">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-violet-600 flex items-center justify-center">
                <Disc3 className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-white">DJ Console</h1>
                <p className="text-sm text-slate-400">{sessionData?.club_name || 'Loading...'}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button
                onClick={() => setShowAnalytics(true)}
                variant="outline"
                size="sm"
                className="border-slate-700 text-slate-300"
                data-testid="open-analytics-btn"
              >
                <BarChart3 className="w-4 h-4 mr-2" />
                Analytics
              </Button>
              <a
                href={livePageUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-3 py-2 bg-slate-800 hover:bg-slate-700 rounded-lg text-slate-300 text-sm transition-colors"
              >
                <Eye className="w-4 h-4" />
                View Live Page
              </a>
              {sessionData?.active && (
                <Button
                  onClick={handleExport}
                  variant="outline"
                  size="sm"
                  className="border-slate-700"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Export
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6">
        {!sessionData?.active ? (
          /* Start Session UI */
          <div className="max-w-2xl mx-auto py-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center mb-8"
            >
              <div className="w-24 h-24 rounded-full bg-slate-800 flex items-center justify-center mx-auto mb-6">
                <Power className="w-12 h-12 text-slate-600" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">Start a Live Session</h2>
              <p className="text-slate-400 mb-6">
                Begin accepting dance requests from your dancers
              </p>
              <div className="space-y-4 max-w-md mx-auto">
                <Input
                  value={sessionName}
                  onChange={(e) => setSessionName(e.target.value)}
                  placeholder="Session name (optional)"
                  className="bg-slate-900 border-slate-700 text-white"
                  data-testid="session-name-input"
                />
                {mode === 'dj' && (
                  <Input
                    value={venue}
                    onChange={(e) => setVenue(e.target.value)}
                    placeholder="Venue / Event name (optional)"
                    className="bg-slate-900 border-slate-700 text-white"
                    data-testid="venue-input"
                  />
                )}
                <Button
                  onClick={startSession}
                  disabled={starting}
                  className="w-full bg-green-600 hover:bg-green-500 text-white"
                  size="lg"
                  data-testid="start-session-btn"
                >
                  {starting ? (
                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  ) : (
                    <Power className="w-5 h-5 mr-2" />
                  )}
                  Start New Session
                </Button>
              </div>
            </motion.div>

            {/* Event Schedule Section */}
            <div className="border-t border-slate-800 pt-8">
              <button
                onClick={() => setShowSchedule(!showSchedule)}
                className="flex items-center justify-between w-full text-left p-4 bg-slate-900/50 rounded-xl hover:bg-slate-900 transition-colors"
                data-testid="toggle-schedule-btn"
              >
                <div className="flex items-center gap-3">
                  <CalendarClock className="w-5 h-5 text-cyan-400" />
                  <div>
                    <span className="font-medium text-white">Event Schedule</span>
                    <p className="text-sm text-slate-400">Show schedule on TV/projector display</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {scheduleEnabled && (
                    <span className="px-2 py-1 bg-cyan-500/20 text-cyan-400 text-xs rounded-full">
                      Active
                    </span>
                  )}
                  {showSchedule ? (
                    <ChevronUp className="w-5 h-5 text-slate-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-slate-400" />
                  )}
                </div>
              </button>
              
              <AnimatePresence>
                {showSchedule && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="overflow-hidden"
                  >
                    <div className="pt-4 space-y-4">
                      {/* Event Date */}
                      <div className="p-4 bg-slate-900 rounded-xl">
                        <label className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-cyan-400" />
                            <span className="text-sm text-slate-300">Event Date</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <input
                              type="date"
                              value={scheduleEventDate}
                              onChange={(e) => {
                                setScheduleEventDate(e.target.value);
                                saveEventDate(e.target.value);
                              }}
                              className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-1.5 text-white text-sm"
                              data-testid="schedule-event-date"
                            />
                            {scheduleEventDate && (
                              <button
                                onClick={() => {
                                  setScheduleEventDate('');
                                  saveEventDate('');
                                }}
                                className="text-slate-400 hover:text-red-400 p-1"
                                title="Clear date"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </label>
                        <p className="text-xs text-slate-500 mt-2">Set the event date for this schedule (useful when setting up the day before)</p>
                      </div>
                      
                      {/* Toggle & Add Form */}
                      <div className="flex items-center justify-between p-4 bg-slate-900 rounded-xl">
                        <span className="text-sm text-slate-300">Show on display</span>
                        <button
                          onClick={toggleScheduleEnabled}
                          disabled={savingSchedule}
                          className={`relative w-12 h-6 rounded-full transition-colors ${
                            scheduleEnabled ? 'bg-cyan-500' : 'bg-slate-700'
                          }`}
                        >
                          <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                            scheduleEnabled ? 'translate-x-7' : 'translate-x-1'
                          }`} />
                        </button>
                      </div>
                      
                      {/* Add new schedule item */}
                      <div className="bg-slate-900 rounded-xl p-4 space-y-3">
                        <h4 className="text-sm font-medium text-white">Add Schedule Item</h4>
                        <div className="grid grid-cols-4 gap-3">
                          <input
                            type="time"
                            value={newScheduleTime}
                            onChange={(e) => setNewScheduleTime(e.target.value)}
                            className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm"
                            placeholder="Time"
                          />
                          <input
                            type="text"
                            value={newScheduleTitle}
                            onChange={(e) => setNewScheduleTitle(e.target.value)}
                            className="col-span-2 bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm"
                            placeholder="Title (e.g., Beginner Class)"
                          />
                          <Button
                            onClick={addScheduleItem}
                            disabled={savingSchedule || !newScheduleTime || !newScheduleTitle.trim()}
                            size="sm"
                            className="bg-cyan-600 hover:bg-cyan-700"
                          >
                            {savingSchedule ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                          </Button>
                        </div>
                        <input
                          type="text"
                          value={newScheduleDesc}
                          onChange={(e) => setNewScheduleDesc(e.target.value)}
                          className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm"
                          placeholder="Description (optional)"
                        />
                      </div>
                      
                      {/* Schedule items list */}
                      {scheduleItems.length === 0 ? (
                        <div className="text-center py-6 text-slate-400">
                          <CalendarClock className="w-8 h-8 mx-auto mb-2 opacity-50" />
                          <p>No schedule items yet</p>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {scheduleItems
                            .sort((a, b) => a.time.localeCompare(b.time))
                            .map((item, index) => (
                            <div
                              key={item.item_id || index}
                              className="flex items-center justify-between p-3 bg-slate-900 rounded-xl"
                            >
                              <div className="flex items-center gap-4">
                                <span className="text-cyan-400 font-mono font-bold text-lg w-14">
                                  {item.time}
                                </span>
                                <div>
                                  <h5 className="text-white font-medium">{item.title}</h5>
                                  {item.description && (
                                    <p className="text-sm text-slate-400">{item.description}</p>
                                  )}
                                </div>
                              </div>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => removeScheduleItem(item.item_id)}
                                className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Session History Section */}
            <div className="border-t border-slate-800 pt-8">
              <button
                onClick={() => setShowHistory(!showHistory)}
                className="flex items-center justify-between w-full text-left p-4 bg-slate-900/50 rounded-xl hover:bg-slate-900 transition-colors"
                data-testid="toggle-history-btn"
              >
                <div className="flex items-center gap-3">
                  <History className="w-5 h-5 text-violet-400" />
                  <div>
                    <span className="font-medium text-white">Session History</span>
                    <p className="text-sm text-slate-400">Recall dances from previous sessions</p>
                  </div>
                </div>
                {showHistory ? (
                  <ChevronUp className="w-5 h-5 text-slate-400" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-slate-400" />
                )}
              </button>
              
              <AnimatePresence>
                {showHistory && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="overflow-hidden"
                  >
                    <div className="pt-4 space-y-3">
                      {loadingHistory ? (
                        <div className="flex items-center justify-center py-8">
                          <Loader2 className="w-6 h-6 text-violet-400 animate-spin" />
                        </div>
                      ) : pastSessions.length === 0 ? (
                        <div className="text-center py-8 text-slate-400">
                          <History className="w-8 h-8 mx-auto mb-2 opacity-50" />
                          <p>No previous sessions found</p>
                        </div>
                      ) : (
                        pastSessions.map((session) => (
                          <div
                            key={session.session_id}
                            className="bg-slate-900 border border-slate-800 rounded-xl p-4 hover:border-slate-700 transition-colors"
                            data-testid={`session-history-${session.session_id}`}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1 min-w-0">
                                <h4 className="font-medium text-white truncate">
                                  {session.session_name || 'Unnamed Session'}
                                </h4>
                                <div className="flex items-center gap-3 text-sm text-slate-400 mt-1">
                                  <span className="flex items-center gap-1">
                                    <Calendar className="w-3.5 h-3.5" />
                                    {new Date(session.started_at).toLocaleDateString('en-GB', {
                                      day: 'numeric',
                                      month: 'short',
                                      year: 'numeric'
                                    })}
                                  </span>
                                  <span className="flex items-center gap-1">
                                    <Music className="w-3.5 h-3.5" />
                                    {session.total_played || 0} dances played
                                  </span>
                                </div>
                              </div>
                              <Button
                                onClick={() => recallSession(session.session_id)}
                                disabled={recalling}
                                size="sm"
                                className="bg-violet-600 hover:bg-violet-500 shrink-0"
                                data-testid={`recall-session-${session.session_id}`}
                              >
                                {recalling ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <>
                                    <RotateCcw className="w-4 h-4 mr-1" />
                                    Recall
                                  </>
                                )}
                              </Button>
                              <Button
                                onClick={() => viewSessionSummary(session.session_id)}
                                disabled={loadingSummary}
                                size="sm"
                                variant="outline"
                                className="border-slate-600 text-slate-300 hover:bg-slate-700 shrink-0"
                                data-testid={`summary-session-${session.session_id}`}
                              >
                                {loadingSummary ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <>
                                    <Share2 className="w-4 h-4 mr-1" />
                                    Summary
                                  </>
                                )}
                              </Button>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        ) : (
          /* Active Session UI */
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Left Column - Now Playing & Queue */}
            <div className="lg:col-span-2 space-y-6">
              {/* Session Status */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-green-500/10 border border-green-500/30 rounded-xl px-4 py-3">
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse" />
                  <span className="text-green-400 font-medium">Session Active</span>
                  <span className="text-slate-400 hidden sm:inline">•</span>
                  <span className="text-slate-400 text-sm hidden sm:inline">{sessionData.session_name}</span>
                </div>
                <div className="flex items-center gap-4 flex-wrap">
                  {/* Accept Requests Toggle */}
                  <div className="flex items-center gap-2">
                    <span className={`text-sm ${sessionData.accepting_requests !== false ? 'text-green-400' : 'text-orange-400'}`}>
                      {sessionData.accepting_requests !== false ? 'Accepting Requests' : 'Requests Paused'}
                    </span>
                    <Switch
                      checked={sessionData.accepting_requests !== false}
                      onCheckedChange={toggleAcceptingRequests}
                      disabled={togglingRequests}
                      data-testid="toggle-accepting-requests"
                    />
                  </div>
                  
                  {/* Schedule Resume Button - only show when paused */}
                  {sessionData.accepting_requests === false && (
                    <Button
                      onClick={() => setShowScheduleResume(true)}
                      variant="outline"
                      size="sm"
                      className="border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/10"
                      data-testid="schedule-resume-btn"
                    >
                      <Clock className="w-4 h-4 mr-1" />
                      {sessionData.scheduled_resume_at ? 'Edit Schedule' : 'Schedule Resume'}
                    </Button>
                  )}
                  
                  {/* Show scheduled time indicator */}
                  {sessionData.scheduled_resume_at && sessionData.accepting_requests === false && (
                    <span className="text-xs text-cyan-400 bg-cyan-500/10 px-2 py-1 rounded">
                      Auto-resume: {new Date(sessionData.scheduled_resume_at).toLocaleString('en-GB', {
                        day: 'numeric',
                        month: 'short',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </span>
                  )}
                  
                  <Button
                    onClick={endSession}
                    disabled={ending}
                    variant="outline"
                    size="sm"
                    className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                  >
                    {ending ? <Loader2 className="w-4 h-4 animate-spin" /> : <PowerOff className="w-4 h-4 mr-1" />}
                    End Session
                  </Button>
                </div>
              </div>

              {/* Now Playing */}
              <div className="bg-gradient-to-r from-violet-600/20 to-purple-600/20 border border-violet-500/30 rounded-2xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                    <Music className="w-5 h-5 text-violet-400" />
                    Now Playing
                  </h3>
                  <div className="flex gap-2">
                    {sessionData.current_dance && (
                      <Button
                        onClick={finishCurrent}
                        size="sm"
                        variant="outline"
                        className="border-green-500/50 text-green-400"
                      >
                        <Check className="w-4 h-4 mr-1" />
                        Finish
                      </Button>
                    )}
                    <Button
                      onClick={playNext}
                      size="sm"
                      className="bg-violet-600 hover:bg-violet-500"
                      disabled={upcomingDances.length === 0}
                    >
                      <SkipForward className="w-4 h-4 mr-1" />
                      Play Next
                    </Button>
                  </div>
                </div>
                
                {sessionData.current_dance ? (
                  <div className="bg-slate-900/50 rounded-xl p-4">
                    <h4 className="text-xl font-bold text-white mb-2">
                      {sessionData.current_dance.dance_name}
                    </h4>
                    <div className="flex flex-wrap items-center gap-3 text-sm">
                      {sessionData.current_dance.level && (
                        <Badge className="bg-violet-500/30 text-violet-200">
                          {sessionData.current_dance.level}
                        </Badge>
                      )}
                      {sessionData.current_dance.count && (
                        <span className="text-slate-400">{sessionData.current_dance.count} Count</span>
                      )}
                      <span className="text-slate-400">•</span>
                      <span className="text-slate-400">
                        Requested by {sessionData.current_dance.requester_name}
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-400">
                    <Disc3 className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No dance playing</p>
                    <p className="text-sm">Select a request or play from queue</p>
                  </div>
                )}
              </div>

              {/* Up Next Queue */}
              <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                    <Clock className="w-5 h-5 text-amber-400" />
                    Up Next ({upcomingDances.length})
                  </h3>
                  <Button
                    onClick={() => setShowAddDance(true)}
                    size="sm"
                    className="bg-green-600 hover:bg-green-500"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add Dance
                  </Button>
                </div>
                
                {/* Manual Add Dance Form */}
                <AnimatePresence>
                  {showAddDance && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="mb-4 overflow-hidden"
                    >
                      <div className="bg-slate-800/80 border border-slate-700 rounded-xl p-4">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="font-medium text-white">Add Dance Manually</h4>
                          <button
                            onClick={() => {
                              setShowAddDance(false);
                              setManualDanceName('');
                              setSelectedManualDance(null);
                            }}
                            className="text-slate-400 hover:text-white"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                        
                        <div className="relative mb-3">
                          <Input
                            value={manualDanceName}
                            onChange={(e) => {
                              setManualDanceName(e.target.value);
                              setSelectedManualDance(null);
                            }}
                            placeholder="Search or type dance name..."
                            className="bg-slate-900 border-slate-600 text-white"
                          />
                          {searchingDance && (
                            <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 animate-spin" />
                          )}
                          
                          {/* Search Results */}
                          {manualDanceSearch.length > 0 && !selectedManualDance && (
                            <div className="absolute z-50 w-full mt-1 bg-slate-800 border border-slate-600 rounded-lg shadow-xl overflow-hidden max-h-80 overflow-y-auto">
                              {manualDanceSearch.map((dance, index) => (
                                <button
                                  key={index}
                                  type="button"
                                  onClick={() => {
                                    setSelectedManualDance(dance);
                                    setManualDanceName(dance.name);
                                    setManualDanceSearch([]);
                                  }}
                                  className="w-full px-4 py-3 text-left hover:bg-slate-700 transition-colors border-b border-slate-700/50 last:border-b-0"
                                >
                                  <p className="font-medium text-white text-sm truncate">{dance.name}</p>
                                  <p className="text-xs text-slate-400 mt-1 truncate">
                                    {dance.choreographer}{dance.choreographer && dance.level ? ' • ' : ''}{dance.level}
                                    {dance.song && <span className="text-slate-400"> • {dance.song}</span>}
                                  </p>
                                  <div className="flex items-center flex-wrap gap-1 mt-1">
                                    {dance.matched_on === 'song' && (
                                      <span className="text-xs px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-300 inline-flex items-center gap-1">
                                        <Music className="w-3 h-3" />Song match
                                      </span>
                                    )}
                                    {dance.source && (
                                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                                        dance.source === 'bootstepper'
                                          ? 'bg-blue-500/20 text-blue-300'
                                          : dance.source === 'copperknob'
                                          ? 'bg-amber-500/20 text-amber-300'
                                          : 'bg-slate-600/50 text-slate-400'
                                      }`}>
                                        {dance.source === 'bootstepper' ? 'BootStepper' :
                                         dance.source === 'copperknob' ? 'CopperKnob' : 'Local'}
                                      </span>
                                    )}
                                  </div>
                                </button>
                              ))}
                            </div>
                          )}
                          
                          {/* Add as typed option - when there are search results but user wants to add custom */}
                          {manualDanceName.trim().length >= 2 && !selectedManualDance && !searchingDance && (
                            <button
                              type="button"
                              onClick={() => {
                                setSelectedManualDance({
                                  name: manualDanceName.trim(),
                                  choreographer: '',
                                  level: '',
                                  song: '',
                                  source: 'manual'
                                });
                                setManualDanceSearch([]);
                              }}
                              className="w-full mt-2 px-3 py-2 text-left bg-slate-700/50 hover:bg-slate-700 rounded-lg transition-colors border border-dashed border-slate-500"
                            >
                              <p className="text-sm text-slate-300">
                                Add "<span className="text-white font-medium">{manualDanceName.trim()}</span>" as typed
                              </p>
                              <p className="text-xs text-slate-400 mt-0.5">Use this if the dance isn't in the search results</p>
                            </button>
                          )}
                        </div>
                        
                        {selectedManualDance && (
                          <div className="flex items-center gap-2 p-2 bg-green-500/10 border border-green-500/30 rounded-lg mb-3">
                            <Check className="w-4 h-4 text-green-400" />
                            <span className="text-sm text-green-300 flex-1 truncate">{selectedManualDance.name}</span>
                            <span className="text-xs text-green-400/70">{selectedManualDance.level}</span>
                          </div>
                        )}
                        
                        <div className="flex gap-2">
                          <Button
                            onClick={() => addManualDance(false)}
                            disabled={!manualDanceName.trim()}
                            size="sm"
                            className="flex-1 bg-amber-600 hover:bg-amber-500"
                          >
                            <Plus className="w-4 h-4 mr-1" />
                            Add to Queue
                          </Button>
                          <Button
                            onClick={() => addManualDance(true)}
                            disabled={!manualDanceName.trim()}
                            size="sm"
                            className="flex-1 bg-green-600 hover:bg-green-500"
                          >
                            <Play className="w-4 h-4 mr-1" />
                            Play Now
                          </Button>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
                
                {upcomingDances.length === 0 ? (
                  <div className="text-center py-8 text-slate-400">
                    <p>Queue is empty</p>
                    <p className="text-sm">Click "Add Dance" or add from requests</p>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center gap-2 mb-2 px-1 text-xs font-semibold uppercase tracking-wide text-emerald-400" data-testid="queue-visible-label">
                      <Eye className="w-3.5 h-3.5" />
                      Visible to dancers — Next {Math.min(4, upcomingDances.length)}
                    </div>
                    <Reorder.Group
                      as="div"
                      axis="y"
                      values={upcomingDances}
                      onReorder={handleReorder}
                      className="space-y-2"
                    >
                      {upcomingDances.map((dance, index) => (
                        <React.Fragment key={dance.request_id}>
                          {index === 4 && (
                            <div className="flex items-center gap-2 mt-3 mb-2 px-1 pt-3 border-t border-slate-700 text-xs font-medium uppercase tracking-wide text-slate-500" data-testid="queue-hidden-label">
                              <EyeOff className="w-3.5 h-3.5" />
                              Not yet visible to dancers
                            </div>
                          )}
                          <Reorder.Item
                            as="div"
                            value={dance}
                            className={`rounded-xl p-4 flex items-center gap-4 cursor-move ${index < 4 ? 'bg-emerald-500/10 ring-1 ring-emerald-500/40' : 'bg-slate-800/50'}`}
                          >
                            <GripVertical className="w-5 h-5 text-slate-600" />
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center font-bold shrink-0 ${index < 4 ? 'bg-emerald-500/20 text-emerald-300' : 'bg-amber-500/20 text-amber-400'}`}>
                              {index + 1}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-white truncate">{dance.dance_name}</p>
                              <p className="text-sm text-slate-400 truncate">
                                {dance.level && `${dance.level} • `}Requested by {dance.requester_name}
                              </p>
                            </div>
                            <div className="flex items-center gap-1 text-violet-300 shrink-0" data-testid={`queue-votes-${dance.request_id}`}>
                              <ThumbsUp className="w-4 h-4" />
                              <span className="text-sm font-bold">{dance.votes ?? 0}</span>
                            </div>
                            <div className="flex gap-2">
                              <Button
                                onClick={() => setNowPlaying(dance)}
                                size="sm"
                                variant="ghost"
                                className="text-green-400 hover:bg-green-500/20"
                              >
                                <Play className="w-4 h-4" />
                              </Button>
                              <Button
                                onClick={() => removeFromQueue(dance.request_id)}
                                size="sm"
                                variant="ghost"
                                className="text-red-400 hover:bg-red-500/20"
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            </div>
                          </Reorder.Item>
                        </React.Fragment>
                      ))}
                    </Reorder.Group>
                  </>
                )}
              </div>

              {/* Played Stats */}
              {sessionData.played_dances?.length > 0 && (
                <div className="bg-slate-900/30 rounded-xl p-4 text-center">
                  <p className="text-slate-400">
                    <span className="text-2xl font-bold text-white mr-2">
                      {sessionData.played_dances.length}
                    </span>
                    dances played this session
                  </p>
                </div>
              )}
            </div>

            {/* Right Column - Requests */}
            <div className="space-y-6">
              {/* Pending Requests */}
              <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-5">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-blue-400" />
                    Requests
                  </span>
                  {sessionData.pending_requests?.length > 0 && (
                    <Badge className="bg-blue-500/20 text-blue-300">
                      {sessionData.pending_requests.length} new
                    </Badge>
                  )}
                </h3>
                
                {sessionData.pending_requests?.length === 0 ? (
                  <div className="text-center py-8 text-slate-400">
                    <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No pending requests</p>
                    <p className="text-sm">Waiting for dancers...</p>
                  </div>
                ) : (
                  <div className="space-y-3 max-h-[500px] overflow-y-auto">
                    {sessionData.pending_requests?.map((request) => (
                      <motion.div
                        key={request.request_id}
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        className="bg-slate-800/50 rounded-xl p-4"
                      >
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-white truncate">{request.dance_name}</p>
                            <p className="text-sm text-slate-400 truncate">
                              by {request.requester_name}
                            </p>
                          </div>
                          <div className="flex items-center gap-1 bg-violet-500/20 px-2 py-1 rounded-lg">
                            <ThumbsUp className="w-3 h-3 text-violet-400" />
                            <span className="text-sm font-bold text-violet-300">{request.votes}</span>
                          </div>
                        </div>
                        
                        {request.level && (
                          <div className="flex gap-2 mb-3">
                            <Badge variant="outline" className="border-slate-700 text-slate-400 text-xs">
                              {request.level}
                            </Badge>
                            {request.count && (
                              <Badge variant="outline" className="border-slate-700 text-slate-400 text-xs">
                                {request.count} Count
                              </Badge>
                            )}
                          </div>
                        )}
                        
                        {request.songs?.length > 0 && (
                          <div className="mb-3" data-testid={`song-selector-${request.request_id}`}>
                            <label className="text-xs text-slate-400 flex items-center gap-1 mb-1">
                              <Music className="w-3 h-3" />
                              Music{request.selected_song?.title ? ` · ${request.selected_song.title}` : ''}
                            </label>
                            <select
                              data-testid={`song-select-${request.request_id}`}
                              value={(() => {
                                const sel = request.selected_song?.music_id || request.selected_song?.title;
                                const idx = request.songs.findIndex(s => (s.music_id || s.title) === sel);
                                return idx >= 0 ? idx : 0;
                              })()}
                              onChange={(e) => selectRequestSong(request.request_id, request.songs[parseInt(e.target.value, 10)])}
                              className="w-full bg-slate-900 border border-slate-600 text-white text-xs rounded-lg px-2 py-1.5"
                            >
                              {request.songs.map((s, i) => (
                                <option key={i} value={i}>
                                  {i === 0 ? '★ ' : ''}{s.title}{s.artist ? ` — ${s.artist}` : ''}{i === 0 ? ' (original)' : ''}
                                </option>
                              ))}
                            </select>
                          </div>
                        )}
                        
                        <div className="flex gap-2">
                          <Button
                            onClick={() => addToQueue(request)}
                            size="sm"
                            className="flex-1 bg-amber-500/20 hover:bg-amber-500/30 text-amber-300"
                          >
                            <Plus className="w-4 h-4 mr-1" />
                            Queue
                          </Button>
                          <Button
                            onClick={() => setNowPlaying(request)}
                            size="sm"
                            className="flex-1 bg-green-500/20 hover:bg-green-500/30 text-green-300"
                          >
                            <Play className="w-4 h-4 mr-1" />
                            Play
                          </Button>
                          <Button
                            onClick={() => declineRequest(request.request_id)}
                            size="sm"
                            variant="ghost"
                            className="text-red-400 hover:bg-red-500/20"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </div>

              {/* Announcement Input */}
              <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-5">
                <h3 className="text-sm font-semibold text-slate-400 mb-3 flex items-center gap-2">
                  <Megaphone className="w-4 h-4" />
                  TV Display Announcement
                </h3>
                <Input
                  value={announcement}
                  onChange={(e) => setAnnouncement(e.target.value)}
                  placeholder="Enter message to show on TV..."
                  className="bg-slate-800 border-slate-700 text-white mb-2"
                  maxLength={200}
                />
                <div className="flex gap-2">
                  <Button
                    onClick={() => saveAnnouncement()}
                    disabled={savingAnnouncement}
                    size="sm"
                    className="flex-1 bg-orange-600 hover:bg-orange-500"
                  >
                    {savingAnnouncement ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Update'}
                  </Button>
                  {announcement && (
                    <Button
                      onClick={async () => {
                        setAnnouncement('');
                        await saveAnnouncement('');
                      }}
                      size="sm"
                      className="bg-slate-700 hover:bg-slate-600 text-white"
                    >
                      Clear
                    </Button>
                  )}
                </div>
                <p className="text-xs text-slate-400 mt-2">Shows as scrolling ticker on TV Display</p>
              </div>

              {/* Share QR */}
              <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-5 text-center">
                <h3 className="text-sm font-semibold text-slate-400 mb-3">Share with Dancers</h3>
                <div className="bg-white rounded-xl p-3 inline-block mb-3">
                  <img 
                    src={`https://api.qrserver.com/v1/create-qr-code/?size=120x120&data=${encodeURIComponent(livePageUrl)}`}
                    alt="QR Code"
                    className="w-[120px] h-[120px]"
                  />
                </div>
                <p className="text-xs text-slate-400 break-all">{livePageUrl}</p>
                <Button
                  onClick={() => {
                    navigator.clipboard.writeText(livePageUrl);
                    toast.success('Link copied!');
                  }}
                  size="sm"
                  className="mt-3 w-full bg-slate-700 hover:bg-slate-600 text-white"
                >
                  Copy Link
                </Button>
                
                {/* TV Display Mode Button */}
                <Button
                  onClick={() => {
                    window.open(displayPageUrl, '_blank', 'noopener,noreferrer');
                  }}
                  variant="outline"
                  size="sm"
                  className="mt-2 w-full border-violet-700 text-violet-300 hover:bg-violet-900/30"
                  data-testid="open-tv-display-btn"
                >
                  <Monitor className="w-4 h-4 mr-2" />
                  Open TV Display
                </Button>
                
                {/* Projector Display Mode Button */}
                <Button
                  onClick={() => {
                    window.open(projectorPageUrl, '_blank', 'noopener,noreferrer');
                  }}
                  variant="outline"
                  size="sm"
                  className="mt-2 w-full border-orange-700 text-orange-300 hover:bg-orange-900/30"
                  data-testid="open-projector-display-btn"
                >
                  <Projector className="w-4 h-4 mr-2" />
                  Open Projector Display
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Export Modal */}
      <AnimatePresence>
        {showExport && exportData && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowExport(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-slate-900 border border-slate-700 rounded-2xl p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-xl font-bold text-white">{exportData.session_name}</h3>
                  <p className="text-sm text-slate-400">
                    {exportData.total_played} dances played
                  </p>
                </div>
                <Button onClick={downloadCSV} className="bg-green-600 hover:bg-green-500">
                  <Download className="w-4 h-4 mr-2" />
                  Download CSV
                </Button>
              </div>
              
              <div className="space-y-2">
                {exportData.played_dances.map((dance, index) => (
                  <div key={index} className="flex items-center gap-4 bg-slate-800/50 rounded-lg p-3">
                    <div className="w-8 h-8 rounded bg-violet-500/20 flex items-center justify-center text-violet-400 font-bold">
                      {index + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-medium truncate">{dance.dance_name}</p>
                      <p className="text-sm text-slate-400 truncate">
                        {dance.level && `${dance.level} • `}by {dance.requester_name}
                      </p>
                    </div>
                    <div className="text-slate-400 text-sm">
                      {dance.votes} votes
                    </div>
                  </div>
                ))}
              </div>
              
              <Button
                onClick={() => setShowExport(false)}
                variant="outline"
                className="w-full mt-6 border-slate-700"
              >
                Close
              </Button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Session Summary Modal */}
      {showSummary && summarySession && (
        <SessionSummary
          session={summarySession}
          clubLogo={sessionData?.club_image || summarySession?.club_image}
          clubName={sessionData?.club_name || summarySession?.club_name || 'DJ Session'}
          onClose={() => {
            setShowSummary(false);
            setSummarySession(null);
          }}
        />
      )}

      {/* Release Notes Modal */}
      <AnimatePresence>
        {showReleaseNotes && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={dismissReleaseNotes}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={e => e.stopPropagation()}
              className="bg-slate-900 border border-slate-700 rounded-2xl p-6 max-w-lg w-full max-h-[80vh] overflow-y-auto"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
                  <Sparkles className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">What's New</h2>
                  <p className="text-sm text-slate-400">DJ Console Updates</p>
                </div>
              </div>

              <div className="space-y-6">
                {RELEASE_NOTES.map((release, idx) => (
                  <div key={release.version} className={idx > 0 ? 'pt-4 border-t border-slate-800' : ''}>
                    <div className="flex items-center gap-2 mb-3">
                      <Badge className="bg-violet-500/20 text-violet-300 border-0">
                        v{release.version}
                      </Badge>
                      <span className="text-sm text-slate-400">{release.date}</span>
                    </div>
                    <ul className="space-y-2">
                      {release.changes.map((change, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm">
                          <span className={`px-1.5 py-0.5 rounded text-xs font-medium flex-shrink-0 mt-0.5 ${
                            change.type === 'new' 
                              ? 'bg-green-500/20 text-green-400' 
                              : change.type === 'improved'
                              ? 'bg-blue-500/20 text-blue-400'
                              : 'bg-amber-500/20 text-amber-400'
                          }`}>
                            {change.type === 'new' ? 'NEW' : change.type === 'improved' ? 'IMPROVED' : 'FIX'}
                          </span>
                          <span className="text-slate-300">{change.text}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>

              <Button
                onClick={dismissReleaseNotes}
                className="w-full mt-6 bg-violet-600 hover:bg-violet-700"
              >
                Got it!
              </Button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Analytics Modal */}
      <AnimatePresence>
        {showAnalytics && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => setShowAnalytics(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={e => e.stopPropagation()}
              className="bg-slate-900 border border-slate-700 rounded-2xl p-6 max-w-4xl w-full max-h-[85vh] overflow-y-auto"
            >
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
                    <BarChart3 className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-white">Session Analytics</h2>
                    <p className="text-sm text-slate-400">Performance insights across all sessions</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowAnalytics(false)}
                  className="text-slate-400 hover:text-white transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {loadingAnalytics ? (
                <div className="flex items-center justify-center py-16">
                  <Loader2 className="w-8 h-8 text-violet-400 animate-spin" />
                </div>
              ) : analyticsData ? (
                <div className="space-y-6">
                  {/* Summary Cards */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-slate-800/50 rounded-xl p-4 text-center">
                      <p className="text-3xl font-bold text-white">{analyticsData.total_sessions}</p>
                      <p className="text-sm text-slate-400">Total Sessions</p>
                    </div>
                    <div className="bg-slate-800/50 rounded-xl p-4 text-center">
                      <p className="text-3xl font-bold text-emerald-400">{analyticsData.total_dances_played}</p>
                      <p className="text-sm text-slate-400">Dances Played</p>
                    </div>
                    <div className="bg-slate-800/50 rounded-xl p-4 text-center">
                      <p className="text-3xl font-bold text-violet-400">{analyticsData.total_requests}</p>
                      <p className="text-sm text-slate-400">Total Requests</p>
                    </div>
                    <div className="bg-slate-800/50 rounded-xl p-4 text-center">
                      <p className="text-3xl font-bold text-amber-400">{analyticsData.avg_dances_per_session}</p>
                      <p className="text-sm text-slate-400">Avg per Session</p>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Top Dances */}
                    <div className="bg-slate-800/30 rounded-xl p-5">
                      <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                        <Music className="w-5 h-5 text-violet-400" />
                        Most Played Dances
                      </h3>
                      {analyticsData.top_dances.length > 0 ? (
                        <div className="space-y-2">
                          {analyticsData.top_dances.slice(0, 5).map((dance, index) => (
                            <div key={index} className="flex items-center gap-3 bg-slate-800/50 rounded-lg p-3">
                              <div className="w-8 h-8 rounded-lg bg-violet-500/20 flex items-center justify-center text-violet-400 font-bold text-sm">
                                {index + 1}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-white truncate">{dance.name}</p>
                                <p className="text-xs text-slate-400">
                                  {dance.level && <span>{dance.level} • </span>}
                                  {dance.count} plays
                                </p>
                              </div>
                              <Badge className="bg-violet-500/20 text-violet-300 border-0">
                                {dance.total_votes} votes
                              </Badge>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-slate-400 text-center py-4">No data yet</p>
                      )}
                    </div>

                    {/* Level Distribution */}
                    <div className="bg-slate-800/30 rounded-xl p-5">
                      <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                        <Users className="w-5 h-5 text-emerald-400" />
                        Level Distribution
                      </h3>
                      {analyticsData.top_levels.length > 0 ? (
                        <div className="space-y-3">
                          {analyticsData.top_levels.map((level, index) => (
                            <div key={index}>
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-sm text-slate-300">{level.level}</span>
                                <span className="text-sm text-slate-400">{level.percentage}%</span>
                              </div>
                              <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                                <div 
                                  className="h-full bg-gradient-to-r from-emerald-500 to-teal-400 rounded-full transition-all"
                                  style={{ width: `${level.percentage}%` }}
                                />
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-slate-400 text-center py-4">No data yet</p>
                      )}
                    </div>
                  </div>

                  {/* Hourly Distribution */}
                  {analyticsData.hourly_distribution.length > 0 && (
                    <div className="bg-slate-800/30 rounded-xl p-5">
                      <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                        <Clock className="w-5 h-5 text-amber-400" />
                        Peak Request Times
                      </h3>
                      <div className="flex items-end gap-1 h-32">
                        {Array.from({ length: 24 }, (_, hour) => {
                          const data = analyticsData.hourly_distribution.find(h => h.hour === hour);
                          const count = data?.count || 0;
                          const maxCount = Math.max(...analyticsData.hourly_distribution.map(h => h.count));
                          const height = maxCount > 0 ? (count / maxCount) * 100 : 0;
                          
                          return (
                            <div key={hour} className="flex-1 flex flex-col items-center gap-1">
                              <div 
                                className="w-full bg-gradient-to-t from-amber-500 to-amber-400 rounded-t transition-all hover:from-amber-400 hover:to-amber-300"
                                style={{ height: `${Math.max(height, 2)}%` }}
                                title={`${hour}:00 - ${count} requests`}
                              />
                              {hour % 4 === 0 && (
                                <span className="text-[10px] text-slate-400">{hour}</span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                      <p className="text-xs text-slate-400 text-center mt-2">Hour of day (24h)</p>
                    </div>
                  )}

                  {/* Recent Sessions */}
                  {analyticsData.session_history.length > 0 && (
                    <div className="bg-slate-800/30 rounded-xl p-5">
                      <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                        <History className="w-5 h-5 text-blue-400" />
                        Recent Sessions
                      </h3>
                      <div className="space-y-2">
                        {analyticsData.session_history.slice(0, 5).map((session, index) => (
                          <div key={index} className="flex items-center justify-between bg-slate-800/50 rounded-lg p-3">
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-white truncate">{session.session_name}</p>
                              <p className="text-xs text-slate-400">
                                {session.started_at && new Date(session.started_at).toLocaleDateString('en-GB', {
                                  day: 'numeric', month: 'short', year: 'numeric'
                                })}
                                {session.duration_minutes && ` • ${session.duration_minutes} mins`}
                              </p>
                            </div>
                            <Badge className={`${session.status === 'ended' ? 'bg-slate-500/20 text-slate-300' : 'bg-green-500/20 text-green-300'} border-0`}>
                              {session.dances_played} dances
                            </Badge>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-slate-400 text-center py-8">No analytics data available</p>
              )}

              <Button
                onClick={() => setShowAnalytics(false)}
                variant="outline"
                className="w-full mt-6 border-slate-700"
              >
                Close
              </Button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Schedule Resume Modal */}
      <AnimatePresence>
        {showScheduleResume && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => setShowScheduleResume(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-slate-900 border border-slate-700 rounded-2xl p-6 max-w-md w-full"
            >
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                  <Clock className="w-6 h-6 text-cyan-400" />
                  Schedule Auto-Resume
                </h2>
                <button
                  onClick={() => setShowScheduleResume(false)}
                  className="text-slate-400 hover:text-white"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <p className="text-slate-400 text-sm mb-6">
                Set a date and time for requests to automatically resume. Perfect for setting up events the day before!
              </p>
              
              <div className="space-y-4">
                <div>
                  <label className="text-sm text-slate-300 mb-1 block">Date</label>
                  <input
                    type="date"
                    value={scheduledResumeDate}
                    onChange={(e) => setScheduledResumeDate(e.target.value)}
                    className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 text-white"
                    min={new Date().toISOString().split('T')[0]}
                    data-testid="scheduled-resume-date"
                  />
                </div>
                
                <div>
                  <label className="text-sm text-slate-300 mb-1 block">Time</label>
                  <input
                    type="time"
                    value={scheduledResumeTime}
                    onChange={(e) => setScheduledResumeTime(e.target.value)}
                    className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 text-white"
                    data-testid="scheduled-resume-time"
                  />
                </div>
                
                {sessionData?.scheduled_resume_at && (
                  <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-3">
                    <p className="text-sm text-cyan-400">
                      Currently scheduled: {new Date(sessionData.scheduled_resume_at).toLocaleString('en-GB', {
                        weekday: 'short',
                        day: 'numeric',
                        month: 'short',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                )}
              </div>
              
              <div className="flex gap-3 mt-6">
                {sessionData?.scheduled_resume_at && (
                  <Button
                    onClick={clearScheduledResume}
                    variant="outline"
                    className="flex-1 border-red-500/50 text-red-400 hover:bg-red-500/10"
                    disabled={savingScheduledResume}
                  >
                    Clear Schedule
                  </Button>
                )}
                <Button
                  onClick={saveScheduledResume}
                  className="flex-1 bg-cyan-600 hover:bg-cyan-700"
                  disabled={savingScheduledResume || !scheduledResumeDate || !scheduledResumeTime}
                  data-testid="save-scheduled-resume"
                >
                  {savingScheduledResume ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    'Save Schedule'
                  )}
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
